<template>
  <div class="proxy-user-picker">
    <a-select
      :value="modelValue"
      :disabled="disabled || creating"
      :loading="loading"
      :options="selectOptions"
      allow-clear
      show-search
      :filter-option="false"
      placeholder="搜索微信昵称或微信号"
      style="min-width:240px;flex:1"
      @search="onSearch"
      @change="onChange"
    />
    <a-button
      v-if="allowQuickCreate !== false"
      :disabled="disabled"
      @click="quickVisible = true"
    >快速创建用户</a-button>
  </div>

  <a-modal
    v-model:open="quickVisible"
    title="快速创建用户"
    ok-text="创建并选择"
    cancel-text="取消"
    :confirm-loading="creating"
    :mask-closable="false"
    @ok="submitQuickCreate"
  >
    <a-form layout="vertical">
      <a-form-item label="微信昵称" required>
        <a-input v-model:value="quickForm.wechatNickname" :maxlength="64" placeholder="请输入微信昵称" />
      </a-form-item>
      <a-form-item label="微信号">
        <a-input v-model:value="quickForm.wechatID" :maxlength="64" placeholder="选填" />
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { message } from 'ant-design-vue';
import { createProxyUser, getProxyUser, listProxyUsers } from '../api/proxyUsers';
import type { ProxyUser } from '../types';
import {
  buildProxyUserSelectOptions,
  parseCreateProxyUserValue,
} from './proxyUserPickerOptions';

const props = defineProps<{
  modelValue: number | null;
  disabled?: boolean;
  allowQuickCreate?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | null): void;
  (e: 'created', user: ProxyUser): void;
}>();

const options = ref<ProxyUser[]>([]);
const loading = ref(false);
const quickVisible = ref(false);
const creating = ref(false);
const quickForm = ref({ wechatNickname: '', wechatID: '' });
const searchKeyword = ref('');
const settledKeyword = ref('');
let searchTimer: number | undefined;
let latestSearchRequest = 0;

const selectOptions = computed(() => buildProxyUserSelectOptions(
  options.value,
  searchKeyword.value,
  settledKeyword.value,
  loading.value,
  creating.value,
));

const loadOptions = async (keyword = '', requestID = ++latestSearchRequest) => {
  loading.value = true;
  try {
    const response = await listProxyUsers({
      status: 'active',
      keyword: keyword.trim() || undefined,
      limit: 100,
      offset: 0,
    });
    if (requestID !== latestSearchRequest) return;
    options.value = response.data.items;
    settledKeyword.value = keyword.trim();
  } catch (error: any) {
    if (requestID === latestSearchRequest) {
      message.error(error?.response?.data?.error?.message || '用户列表加载失败');
    }
  } finally {
    if (requestID === latestSearchRequest) loading.value = false;
  }
};

const onSearch = (keyword: string) => {
  searchKeyword.value = keyword;
  if (searchTimer !== undefined) window.clearTimeout(searchTimer);
  const requestID = ++latestSearchRequest;
  loading.value = true;
  searchTimer = window.setTimeout(() => void loadOptions(keyword, requestID), 250);
};

const createFromSearch = async (nickname: string) => {
  if (creating.value) return;
  creating.value = true;
  try {
    const response = await createProxyUser({ wechat_nickname: nickname });
    selectUser(response.data);
    emit('created', response.data);
    searchKeyword.value = '';
    settledKeyword.value = '';
    await loadOptions('');
  } catch (error: any) {
    message.error(error?.response?.data?.error?.message || '创建用户失败');
  } finally {
    creating.value = false;
  }
};

const onChange = (value: number | string | undefined) => {
  const nickname = parseCreateProxyUserValue(value);
  if (nickname) {
    void createFromSearch(nickname);
    return;
  }
  if (typeof value === 'number' || value === undefined) {
    emit('update:modelValue', value ?? null);
  }
};

const selectUser = (user: ProxyUser) => {
  options.value = [user, ...options.value.filter((item) => item.id !== user.id)];
  emit('update:modelValue', user.id);
};

const submitQuickCreate = async () => {
  const nickname = quickForm.value.wechatNickname.trim();
  if (!nickname) {
    message.error('请填写微信昵称');
    return;
  }
  creating.value = true;
  try {
    const response = await createProxyUser({
      wechat_nickname: nickname,
      wechat_id: quickForm.value.wechatID.trim() || undefined,
    });
    selectUser(response.data);
    emit('created', response.data);
    quickVisible.value = false;
    quickForm.value = { wechatNickname: '', wechatID: '' };
  } catch (error: any) {
    const code = error?.response?.data?.error?.code;
    const existingID = Number(error?.response?.data?.existing_user_id || 0);
    if (code === 'WECHAT_ID_EXISTS' && existingID > 0) {
      try {
        const existing = (await getProxyUser(existingID)).data;
        if (existing.status === 'active') {
          selectUser(existing);
          quickVisible.value = false;
          message.info('该微信号已存在，已选择已有用户');
        } else {
          message.error('该微信号已存在但用户已停用，请先恢复用户');
        }
      } catch {
        message.error('该微信号已存在');
      }
    } else {
      message.error(error?.response?.data?.error?.message || '创建用户失败');
    }
  } finally {
    creating.value = false;
  }
};

onMounted(() => void loadOptions());
</script>

<style scoped>
.proxy-user-picker {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}
</style>
